import { computed, ref } from "vue";
import httpManager from "@/app/shared/utils/httpManager";

export interface ApiCloudUsage {
  period: string;
  calls_count: number;
  included_calls: number | null;
  overage_calls: number;
  overage_price: number | string | null;
  total_price: number | string;
  /** `Customer.api_cloud_type_billing`: 1 = Prepago, 2 = Postpago. */
  api_cloud_type_billing?: number | null;
  /** Fuente prepago/postpago propia de API Cloud (`is_api_cloud_pre_payment`). */
  is_pre_payment?: boolean | null;
  /**
   * Tramo gratuito base del plan (`configuration.api_cloud_calls.value`), sin el monto
   * configurado. Lo expone el endpoint para separar el consumo gratuito del facturable
   * sin hardcodear el valor.
   */
  free_calls?: number | null;
}

export enum ApiCloudUsageState {
  Ok = "ok",
  Low = "low",
  Exhausted = "exhausted",
}

// Umbral de aviso: quedan menos de estas API calls.
export const LOW_API_CALLS_THRESHOLD = 2000;

export const fetchApiCloudUsage = async (period?: string): Promise<ApiCloudUsage[]> => {
  const response = await httpManager.get("/billing/charges/api-cloud/", {
    params: period ? { period } : undefined,
  });
  return response.data ?? [];
};

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export const useApiCloudUsage = () => {
  const usages = ref<ApiCloudUsage[]>([]);
  const loading = ref(false);

  const fetchUsages = async () => {
    loading.value = true;
    try {
      usages.value = await fetchApiCloudUsage();
    } catch (error) {
      console.error("No se pudo obtener el consumo de API calls", error);
      usages.value = [];
    } finally {
      loading.value = false;
    }
  };

  // El backend solo devuelve filas de periodos con consumo: sin fila = 0 calls.
  const current = computed(() => usages.value.find((u) => u.period === currentPeriod()));
  const included = computed(() => usages.value[0]?.included_calls ?? null);
  const used = computed(() => current.value?.calls_count ?? 0);
  const available = computed(() =>
    included.value === null ? null : Math.max(0, included.value - used.value)
  );
  const consumedPercent = computed(() =>
    included.value ? Math.min(100, Math.round((used.value / included.value) * 100)) : 0
  );
  const state = computed(() => {
    if (available.value === null) return ApiCloudUsageState.Ok;
    if (available.value === 0) return ApiCloudUsageState.Exhausted;
    if (available.value < LOW_API_CALLS_THRESHOLD) return ApiCloudUsageState.Low;
    return ApiCloudUsageState.Ok;
  });

  return { usages, loading, fetchUsages, included, used, available, consumedPercent, state };
};
